/**
 * Bundle 171 hotfix - RSVP payment-step "Have a code?" toggle.
 *
 * Collapsed by default. On expand, admin can type an 8-char code and
 * hit Apply. Applied codes update the parent's price display via
 * `onApplied(applied)` where applied is
 * `{ code, discount_cents, final_price_cents, tier_price_cents }`.
 * "Remove code" reverts via `onApplied(null)`.
 *
 * Validation is preview-only via POST /api/rsvp/apply-golden-ticket.
 * The actual commit fires on RSVP finalize with the applied code
 * threaded through the parent.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Ticket, X, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import api from '@/utils/api';

const ERROR_STRINGS = {
  CODE_NOT_FOUND: 'Code not recognized',
  CODE_EXPIRED: 'This code has expired',
  CODE_EXHAUSTED: 'This code is fully redeemed',
  CODE_ALREADY_USED_BY_YOU: "You've already used this code",
  CODE_NOT_FOR_THIS_EVENT: "This code isn't valid for this event",
  CODE_EXCEEDS_PRICE: 'This code discount is higher than the current price',
  CODE_INACTIVE: 'This code is no longer active',
  CODE_NOT_YET_VALID: 'This code is not active yet',
};

const fmtCents = (c, cur = 'USD') => (
  `$${(c / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} ${(cur || '').toUpperCase()}`);

export const mapErrorCode = (code) => (
  ERROR_STRINGS[code] || 'Code could not be applied');

const GoldenTicketApply = ({ eventId, tierPriceCents, applied, onApplied, currency }) => {
  const [open, setOpen] = useState(!!applied);
  const [code, setCode] = useState(applied?.code || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const apply = async () => {
    setError('');
    const c = (code || '').trim().toUpperCase();
    if (c.length !== 8) {
      setError('Codes are 8 characters');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/rsvp/apply-golden-ticket', {
        event_id: eventId,
        code: c,
        tier_price_cents: tierPriceCents,
      });
      if (typeof onApplied === 'function') onApplied({
        code: data.code,
        discount_cents: data.discount_cents,
        final_price_cents: data.final_price_cents,
        tier_price_cents: data.tier_price_cents,
      });
    } catch (e) {
      const errCode = e?.response?.data?.detail?.error_code;
      setError(mapErrorCode(errCode));
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    if (typeof onApplied === 'function') onApplied(null);
    setCode('');
    setError('');
  };

  if (applied) {
    return (
      <div
        className="rounded-lg border border-secondary-200 bg-secondary-50/60 p-3 flex items-center justify-between"
        data-testid="gt-applied-chip"
      >
        <div className="flex items-center gap-2 text-sm">
          <Ticket className="h-4 w-4 text-secondary-700" />
          <span className="font-mono font-semibold text-primary-900">
            {applied.code}
          </span>
          <span className="text-secondary-800 text-xs uppercase tracking-widest">
            {`-${fmtCents(applied.discount_cents, currency)}`}
          </span>
        </div>
        <Button
          variant="ghost" size="sm"
          onClick={remove}
          className="text-red-700 hover:bg-red-50"
          data-testid="gt-remove-code"
        >
          <X className="h-4 w-4 mr-1" /> Remove
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50/60">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-neutral-100/60"
        data-testid="gt-toggle"
      >
        <span className="flex items-center gap-2 text-primary-900">
          <Ticket className="h-4 w-4 text-secondary-700" />
          Have a golden ticket code?
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-neutral-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-neutral-500" />
        )}
      </button>
      {open ? (
        <div className="px-3 pb-3 pt-1 space-y-2" data-testid="gt-input-row">
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="8-character code"
              value={code}
              maxLength={8}
              onChange={(e) => setCode(
                e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              className="font-mono uppercase"
              data-testid="gt-code-input"
            />
            <Button
              onClick={apply} disabled={busy || code.length !== 8}
              className="bg-primary-900 text-white hover:bg-primary-800"
              data-testid="gt-apply-button"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : 'Apply'}
            </Button>
          </div>
          {error ? (
            <div
              className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1"
              data-testid="gt-apply-error"
              role="alert"
            >
              {error}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default GoldenTicketApply;
